import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isGuestActor } from "../helpers/guestMode";
import {
  CONNECTIVITY_CHANGED_EVENT,
  CONNECTIVITY_STATE,
  isOffline,
} from "../helpers/connectivity";
import {
  loadProfilePreferencesForActor,
  saveProfilePreferences,
} from "../helpers/profilePreferencesHelpers";
import {
  DEFAULT_USER_PREFERENCES,
  isVoiceFirstNavigation,
  mergeUserPreferences,
} from "../helpers/userPreferences";
import {
  loadCachedUserPreferences,
  persistCachedUserPreferences,
  resolveUserPreferencesCacheId,
} from "../helpers/userPreferencesBridge";

const UserPreferencesContext = createContext(null);

export function UserPreferencesProvider({ actor, children }) {
  const cacheUserId = resolveUserPreferencesCacheId(actor);
  const [preferences, setPreferences] = useState(() =>
    loadCachedUserPreferences(cacheUserId),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const profileIdRef = useRef(actor?.profile_id ?? null);

  const applyPreferencesState = useCallback(
    (nextPreferences, profileId = null) => {
      setPreferences(nextPreferences);
      persistCachedUserPreferences(nextPreferences, cacheUserId);
      if (profileId) {
        profileIdRef.current = profileId;
      }
    },
    [cacheUserId],
  );

  const refreshPreferences = useCallback(async () => {
    if (!actor || isGuestActor(actor)) {
      applyPreferencesState({ ...DEFAULT_USER_PREFERENCES });
      profileIdRef.current = null;
      return;
    }

    if (isOffline()) {
      setPreferences(loadCachedUserPreferences(cacheUserId));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await loadProfilePreferencesForActor(actor);
      if (!result) {
        setPreferences(loadCachedUserPreferences(cacheUserId));
        return;
      }

      applyPreferencesState(result.preferences, result.profileId);
    } catch (err) {
      console.warn("[UserPreferencesProvider] load failed — using cache", err);
      setError(err);
      setPreferences(loadCachedUserPreferences(cacheUserId));
    } finally {
      setLoading(false);
    }
  }, [actor, applyPreferencesState, cacheUserId]);

  useEffect(() => {
    refreshPreferences();
  }, [refreshPreferences]);

  useEffect(() => {
    const handleConnectivityRestored = (event) => {
      if (event.detail?.state === CONNECTIVITY_STATE.GOOD) {
        refreshPreferences();
      }
    };

    window.addEventListener(CONNECTIVITY_CHANGED_EVENT, handleConnectivityRestored);
    return () => {
      window.removeEventListener(
        CONNECTIVITY_CHANGED_EVENT,
        handleConnectivityRestored,
      );
    };
  }, [refreshPreferences]);

  const updatePreferences = useCallback(
    async (patch) => {
      const merged = mergeUserPreferences(preferences, patch);
      applyPreferencesState(merged);

      if (!profileIdRef.current || isGuestActor(actor)) {
        return merged;
      }

      if (isOffline()) {
        return merged;
      }

      try {
        const saved = await saveProfilePreferences(profileIdRef.current, merged);
        applyPreferencesState(saved.preferences, saved.profileId);
        return saved.preferences;
      } catch (err) {
        console.warn("[UserPreferencesProvider] save failed — cached locally", err);
        setError(err);
        return merged;
      }
    },
    [actor, applyPreferencesState, preferences],
  );

  const value = useMemo(
    () => ({
      preferences,
      loading,
      error,
      updatePreferences,
      refreshPreferences,
      isVoiceFirstNavigation: isVoiceFirstNavigation(preferences),
    }),
    [preferences, loading, error, updatePreferences, refreshPreferences],
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error("useUserPreferences must be used within UserPreferencesProvider");
  }
  return context;
}

export function useUserPreferencesOptional() {
  return useContext(UserPreferencesContext);
}
